<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSidebarStore } from '@/stores/sidebar'
import { useProjectsStore } from '@/stores/projects'
import { supportedReviewFeatures } from '@/domain/projectFeatures'

const sidebar = useSidebarStore()
const auth = useAuthStore()
const route = useRoute()
const projects = useProjectsStore()

interface NavItem {
  routeName: string
  label: string
  icon: string
  testId: string
}

const allItems: NavItem[] = [
  // Overview is the action-first operator home: the former Dashboard tab's
  // tiles now render as the Overview progress strip, so there's no separate
  // Dashboard nav item.
  {
    routeName: 'project-overview',
    label: 'Project Overview',
    icon: 'pi pi-th-large',
    testId: 'project-nav-overview',
  },
  {
    routeName: 'project-files',
    label: 'Source Files',
    icon: 'pi pi-file-pdf',
    testId: 'project-nav-files',
  },
  {
    routeName: 'project-extractions',
    label: 'Quality Control',
    icon: 'pi pi-check-square',
    testId: 'project-nav-extractions',
  },
]

const items = computed(() => {
  const supportedItems = allItems.filter(
    (item) =>
      item.routeName !== 'project-extractions' ||
      supportedReviewFeatures(projects.featuresFor(Number(route.params.id))).length > 0,
  )
  return auth.isQaQcSpecialist
    ? supportedItems.filter((item) =>
        ['project-files', 'project-extractions', 'project-review-workbook'].includes(
          item.routeName,
        ),
      )
    : supportedItems
})

const projectId = computed(() => String(route.params.id ?? ''))

function isItemActive(item: NavItem): boolean {
  return (
    route.name === item.routeName ||
    (item.routeName === 'project-extractions' &&
      (route.name === 'project-review-workbook' ||
        String(route.name).startsWith('project-extraction-'))) ||
    (item.routeName === 'project-files' && route.name === 'project-file')
  )
}
</script>

<template>
  <nav
    class="project-nav"
    :class="{ 'project-nav--collapsed': sidebar.isCollapsed }"
    aria-label="Project sections"
  >
    <RouterLink
      v-for="item in items"
      :key="item.routeName"
      v-tooltip.right="{ value: sidebar.isCollapsed ? item.label : undefined, showDelay: 300 }"
      class="project-nav__item"
      :class="{ 'project-nav__item--active': isItemActive(item) }"
      :to="{ name: item.routeName, params: { id: projectId } }"
      :aria-current="isItemActive(item) ? 'page' : undefined"
      :data-testid="item.testId"
    >
      <span class="project-nav__icon-slot">
        <i :class="item.icon" aria-hidden="true" />
      </span>
      <span class="project-nav__label">{{ item.label }}</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.project-nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-0_5);
}

.project-nav__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--nav-item-padding-y) var(--space-3);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  font: inherit;
  text-align: left;
  width: 100%;
  text-decoration: none;
}

.project-nav__item i {
  font-size: var(--font-size-nav);
  width: var(--icon-size-nav);
  text-align: center;
  flex-shrink: 0;
}

/* Anchors the collapsed-mode corner-overlay dot on the icon, not the
   row. Matches the pattern in SidebarNav.vue. */
.project-nav__icon-slot {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.project-nav__item:hover {
  background: var(--bg-card-hover);
  color: var(--text-primary);
}

.project-nav__item:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-inset);
}

.project-nav__item--active {
  background: var(--green-faint);
  color: var(--green-primary);
}

html.moria-dark .project-nav__item--active {
  color: var(--green-light);
}

.project-nav__label {
  overflow: hidden;
  white-space: nowrap;
  max-width: var(--sidebar-content-max-w);
  opacity: 1;
  transition:
    max-width var(--duration-slow) var(--ease-default),
    opacity var(--duration-slow) var(--ease-default);
}

.project-nav--collapsed .project-nav__item {
  justify-content: center;
  padding: var(--nav-item-padding-y) 0;
  gap: 0;
}

.project-nav--collapsed .project-nav__label {
  max-width: 0;
  opacity: 0;
}
</style>
